import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "../../supabase/supabase";
import { Copy, Loader2, Plus, RefreshCw, Search } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

type Organization = {
  id: string;
  name: string;
  created_at: string;
  is_pro: boolean;
  member_count?: number;
};

const SuperAdminPage: React.FC = () => {
  const navigate = useNavigate();
  // null while the check runs; platform admins are listed in the platform_admins table and the
  // database enforces it (organizations are only visible to them through RLS)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState<
    Organization[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // New organization dialog: create the client's org, then invite its admin as owner
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [ownerInviteLink, setOwnerInviteLink] = useState<string | null>(null);

  useEffect(() => {
    supabase.rpc("is_platform_admin").then(({ data, error }) => {
      if (error) console.error("Error checking platform admin:", error);
      const allowed = data === true;
      setIsPlatformAdmin(allowed);
      if (allowed) fetchOrganizations();
    });
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredOrganizations(organizations);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredOrganizations(
        organizations.filter(
          (org) =>
            org.name.toLowerCase().includes(query) ||
            org.id.toLowerCase().includes(query),
        ),
      );
    }
  }, [searchQuery, organizations]);

  const fetchOrganizations = async () => {
    setIsLoading(true);
    try {
      // Fetch organizations
      const { data: orgsData, error: orgsError } = await supabase
        .from("organizations")
        .select("*")
        .order("created_at", { ascending: false });

      if (orgsError) throw orgsError;

      // For each organization, get the member count
      const orgsWithMemberCount = await Promise.all(
        orgsData.map(async (org) => {
          const { count, error: membersError } = await supabase
            .from("organization_members")
            .select("*", { count: "exact", head: true })
            .eq("organization_id", org.id);

          return {
            ...org,
            member_count: membersError ? 0 : count || 0,
          };
        }),
      );

      setOrganizations(orgsWithMemberCount);
      setFilteredOrganizations(orgsWithMemberCount);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      toast({
        title: "Error",
        description: "Failed to fetch organizations",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleProStatus = async (orgId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from("organizations")
        .update({ is_pro: !currentStatus })
        .eq("id", orgId);

      if (error) throw error;

      // Update local state
      const updatedOrgs = organizations.map((org) =>
        org.id === orgId ? { ...org, is_pro: !currentStatus } : org,
      );
      setOrganizations(updatedOrgs);
      setFilteredOrganizations(
        filteredOrganizations.map((org) =>
          org.id === orgId ? { ...org, is_pro: !currentStatus } : org,
        ),
      );

      toast({
        title: "Success",
        description: `Organization PRO status ${!currentStatus ? "enabled" : "disabled"}`,
        variant: "default",
      });
    } catch (error) {
      console.error("Error toggling PRO status:", error);
      toast({
        title: "Error",
        description: "Failed to update PRO status",
        variant: "destructive",
      });
    }
  };

  const openCreateDialog = () => {
    setNewOrgName("");
    setOwnerEmail("");
    setOwnerInviteLink(null);
    setIsCreateOpen(true);
  };

  // Creates the organization (the platform admin becomes an owner, so they can set it up), then
  // invites the client's admin as owner. No email is sent: the invite link is shown to copy.
  const createOrganization = async () => {
    const name = newOrgName.trim();
    const email = ownerEmail.trim().toLowerCase();
    if (!name) return;

    setIsCreating(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error("No active session found");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create_organization`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ name }),
        },
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.error || "Failed to create organization");
      }
      const organizationId: string = result.organization.id;

      await fetchOrganizations();

      if (!email) {
        toast({ title: "Organization created", description: name });
        setIsCreateOpen(false);
        return;
      }

      const { data: invite, error: inviteError } =
        await supabase.functions.invoke("invite_organization_member", {
          body: { organizationId, email, role: "owner" },
        });
      if (inviteError || !invite?.inviteLink) {
        toast({
          title: "Organization created, invitation failed",
          description: `Invite ${email} from the organization's Members page.`,
          variant: "destructive",
        });
        setIsCreateOpen(false);
        return;
      }

      setOwnerInviteLink(invite.inviteLink);
      toast({
        title: "Organization created",
        description: `Send the invite link to ${email}`,
      });
    } catch (error) {
      console.error("Error creating organization:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to create organization",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (isPlatformAdmin === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isPlatformAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-[400px]">
          <CardHeader>
            <CardTitle className="text-center">Not authorized</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              This page is only for Voxi platform admins.
            </p>
            <Button className="w-full" onClick={() => navigate("/")}>
              Back to dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Super Admin Dashboard</h1>
          <p className="text-muted-foreground">Manage all organizations</p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            New organization
          </Button>
          <Button
            variant="outline"
            onClick={fetchOrganizations}
            disabled={isLoading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            {isLoading ? "Loading..." : "Refresh"}
          </Button>
        </div>
      </div>

      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search organizations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization Name</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead>Members</TableHead>
                <TableHead>PRO Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                    <p className="mt-2 text-muted-foreground">
                      Loading organizations...
                    </p>
                  </TableCell>
                </TableRow>
              ) : filteredOrganizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <p className="text-muted-foreground">
                      {searchQuery
                        ? "No organizations match your search"
                        : "No organizations found"}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrganizations.map((org) => (
                  <TableRow
                    key={org.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/organization-features/${org.id}`)}
                  >
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {org.id}
                    </TableCell>
                    <TableCell>
                      {new Date(org.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{org.member_count || 0}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          org.is_pro
                            ? "bg-good-bg text-good dark:bg-good-bg dark:text-good"
                            : "bg-muted text-foreground dark:bg-muted dark:text-foreground dark:text-ink-faint"
                        }`}
                      >
                        {org.is_pro ? "PRO" : "Standard"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click event
                          toggleProStatus(org.id, org.is_pro);
                        }}
                      >
                        {org.is_pro ? "Disable PRO" : "Enable PRO"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New organization</DialogTitle>
            <DialogDescription>
              Create a client's organization and invite their admin as owner.
              You are added as an owner too, so you can set it up.
            </DialogDescription>
          </DialogHeader>

          {ownerInviteLink ? (
            <div className="space-y-2 py-2">
              <Label htmlFor="owner-invite-link">
                Invite link for {ownerEmail.trim().toLowerCase()}
              </Label>
              <div className="flex space-x-2">
                <Input
                  id="owner-invite-link"
                  value={ownerInviteLink}
                  readOnly
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(ownerInviteLink);
                    toast({ title: "Invite link copied" });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                No email is sent: share this link with them. They set a
                password and land in the organization as owner.
              </p>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="new-org-name">Organization name</Label>
                <Input
                  id="new-org-name"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  placeholder="Client name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner-email">Client admin email (optional)</Label>
                <Input
                  id="owner-email"
                  type="email"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  placeholder="admin@client.com"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {ownerInviteLink ? (
              <Button onClick={() => setIsCreateOpen(false)}>Done</Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsCreateOpen(false)}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={createOrganization}
                  disabled={isCreating || !newOrgName.trim()}
                >
                  {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPage;
